import type { CountryLanguages } from '~/types/countryLanguages';
import type { StoryblokConfigSlug } from '~/types/storyblokConfigSlug';
import { findSectionConfigById } from '~/utils/findSectionConfigById';
import { parseLocaleFromPath } from '~/utils/parseLocaleFromPath';
import { resolveSectionPath } from '~/utils/resolveSectionPath';

/**
 * Last path segment of the consent waiting page, e.g. `/de-de/academ/consent-pending`.
 *
 * The section slug in front of it comes from the section registry and is not `academy` everywhere;
 * in DE it is currently `academ`. Resolve these paths from the registry rather than building them.
 */
export const ACADEMY_CONSENT_PENDING_SEGMENT = 'consent-pending';

/**
 * The waiting page uses a wildcard section path so it works for every localized Academy slug. The
 * wildcard also matches other sections, so the route is validated against the section registry;
 * without it, `/de-de/fachportal/consent-pending` would render the page too.
 */
export function isValidAcademyConsentPendingPath(
    registry: StoryblokConfigSlug[] | null | undefined,
    path: string,
    countryLanguages: CountryLanguages
): boolean {
    const locale = parseLocaleFromPath(path);
    if (!locale?.country || !locale.language) return false;

    const segments = path.split('/').filter(Boolean);

    if (segments.at(-1) !== ACADEMY_CONSENT_PENDING_SEGMENT) return false;

    const requestedSectionPath = segments.slice(1, -1).join('/');
    const academyConfig = findSectionConfigById(registry, locale.country, 'academy');
    const academyPath = academyConfig ? resolveSectionPath(academyConfig, locale.language, countryLanguages) : null;

    return academyPath !== null && requestedSectionPath === academyPath;
}
